import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SiteShell } from "@/components/site/SiteShell";
import { AuthBackdrop } from "@/components/site/AuthBackdrop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldAlert, Users } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { supabase } from "@/integrations/supabase/client";
import { requestPasswordResetByPhone, signInWithPhonePassword } from "@/lib/auth.functions";
import { signInAdminByPassword } from "@/lib/admin.functions";
import { signInModeratorWithPassword } from "@/lib/admin-active.functions";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Login — Online Tax VAT Bangladesh" },
      { name: "description", content: "Sign in to your Online Tax VAT Bangladesh account to track tax filings, download invoices, manage VAT submissions and access your secure customer dashboard." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/login" }],
  }),
  validateSearch: (search: Record<string, unknown>) => {
    const next = typeof search.next === "string" && search.next.startsWith("/") && !search.next.startsWith("//") ? search.next : undefined;
    return next ? { next } : ({} as { next?: string });
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const goNext = () => {
    if (next) window.location.href = next;
    else navigate({ to: "/" });
  };
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminShowPw, setAdminShowPw] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminTab, setAdminTab] = useState<"admin" | "moderator">("admin");

  async function onAdminSubmit(e: React.FormEvent, mode: "admin" | "moderator") {
    e.preventDefault();
    setAdminBusy(true);
    try {
      let res: any;
      if (mode === "admin") {
        res = await signInAdminByPassword({ data: { password: adminPassword } });
      } else {
        res = await signInModeratorWithPassword({ data: { email: adminEmail.trim(), password: adminPassword } });
      }
      if (!res.ok) { toast.error(res.error); return; }
      const { error } = await supabase.auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      if (error) { toast.error("Login failed"); return; }
      try {
        // Lifetime admin session: never ask to log in again.
        const until = Date.now() + 100 * 365 * 24 * 60 * 60 * 1000;
        localStorage.setItem("otv_remember_until", String(until));
        sessionStorage.removeItem("otv_session_only");
      } catch {}
      toast.success(mode === "moderator" ? "Welcome, Moderator!" : "Welcome back, Admin!");
      setAdminOpen(false);
      if (next) { window.location.href = next; return; }
      navigate({ to: "/admin" });
    } catch (err: any) {
      toast.error(err?.message ?? "Login failed");
    } finally {
      setAdminBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await signInWithPhonePassword({
        data: { phone: phone.trim(), password },
      });
      if (!res.ok) { toast.error(res.error); return; }
      const { error } = await supabase.auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      });
      if (error) { toast.error(t("auth.login.failedToast")); return; }
      try {
        if (rememberMe) {
          const until = Date.now() + 30 * 24 * 60 * 60 * 1000;
          localStorage.setItem("otv_remember_until", String(until));
          sessionStorage.removeItem("otv_session_only");
        } else {
          localStorage.removeItem("otv_remember_until");
          sessionStorage.setItem("otv_session_only", "1");
        }
      } catch {}
      toast.success(t("auth.login.welcomeToast"));
      goNext();
    } catch (err: any) {
      toast.error(err?.message ?? t("auth.login.failedToast"));
    } finally {
      setLoading(false);
    }
  }

  async function onForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!forgotPhone.trim()) return;
    setForgotBusy(true);
    try {
      await requestPasswordResetByPhone({
        data: {
          phone: forgotPhone.trim(),
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      toast.success(t("auth.login.resetSent"));
      setForgotOpen(false);
      setForgotPhone("");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to request reset");
    } finally {
      setForgotBusy(false);
    }
  }

  return (
    <SiteShell>
      <AuthBackdrop />
      <section className="pt-14 md:pt-20 pb-20 relative">
        <div className="container-page max-w-md relative">
          <div className="glass-strong rounded-3xl p-8 shadow-glow">
            <div className="flex justify-center"><Logo size="lg" /></div>
            <h1 className="mt-6 font-display font-bold text-2xl text-center">{t("auth.login.welcomeTitle")}</h1>
            <p className="text-sm text-muted-foreground text-center mt-1">{t("auth.login.welcomeSubtitle")}</p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="phone">{t("auth.login.phoneLabel")}</Label>
                <Input id="phone" type="tel" required value={phone}
                  placeholder={t("auth.login.phonePlaceholder")}
                  onChange={(e) => setPhone(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="password">{t("auth.login.passwordLabel")}</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={showPassword ? "text" : "password"} required value={password}
                    placeholder={t("auth.login.passwordPlaceholder")}
                    onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                  <button type="button" onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox
                    checked={rememberMe}
                    onCheckedChange={(v) => setRememberMe(v === true)}
                  />
                  <span>{t("auth.login.rememberMe")}</span>
                </label>
                <button type="button" onClick={() => setForgotOpen(true)}
                  className="text-sm text-primary font-semibold hover:underline">
                  {t("auth.login.forgot")}
                </button>
              </div>
              <Button type="submit" disabled={loading} className="w-full gradient-primary text-primary-foreground shadow-glow">
                {loading ? t("auth.login.submitting") : t("auth.login.submit")}
              </Button>
            </form>
            <p className="mt-6 text-sm text-center text-muted-foreground">
              {t("auth.login.noAccount")} <Link to="/signup" className="text-primary font-semibold hover:underline">{t("auth.login.signupLink")}</Link>
            </p>
            <div className="mt-4 pt-4 border-t border-border/60 text-center">
              <button
                type="button"
                onClick={() => setAdminOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Official Admin Login
              </button>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("auth.login.resetTitle")}</DialogTitle>
            <DialogDescription>
              {t("auth.login.resetDesc")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onForgotSubmit} className="space-y-4">
            <div>
              <Label htmlFor="forgot-phone">{t("auth.login.phoneNumber")}</Label>
              <Input id="forgot-phone" type="tel" required placeholder="01XXXXXXXXX"
                value={forgotPhone} onChange={(e) => setForgotPhone(e.target.value)} className="mt-1.5" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" disabled={forgotBusy} className="gradient-primary text-primary-foreground">
                {forgotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.login.sendResetLink")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Official Admin Login</DialogTitle>
            <DialogDescription>
              Restricted access. Authorised personnel only.
            </DialogDescription>
          </DialogHeader>
          <Tabs value={adminTab} onValueChange={(v) => { setAdminTab(v as "admin" | "moderator"); setAdminEmail(""); setAdminPassword(""); }}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="admin" className="gap-1.5"><ShieldAlert className="h-4 w-4" /> Main Admin</TabsTrigger>
              <TabsTrigger value="moderator" className="gap-1.5"><Users className="h-4 w-4" /> Moderator</TabsTrigger>
            </TabsList>
            {(["admin", "moderator"] as const).map((mode) => (
              <TabsContent key={mode} value={mode} className="mt-4">
                <form onSubmit={(e) => onAdminSubmit(e, mode)} className="space-y-4">
                  {mode === "moderator" && (
                    <div>
                      <Label htmlFor={`${mode}-email`}>Email</Label>
                      <Input id={`${mode}-email`} type="email" required
                        placeholder="moderator@example.com"
                        value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="mt-1.5" autoComplete="off" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor={`${mode}-password`}>Password</Label>
                    <div className="relative mt-1.5">
                      <Input id={`${mode}-password`} type={adminShowPw ? "text" : "password"} required
                        value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} className="pr-10" autoComplete="off" />
                      <button type="button" onClick={() => setAdminShowPw((s) => !s)}
                        aria-label={adminShowPw ? "Hide password" : "Show password"}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                        {adminShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setAdminOpen(false)}>{t("common.cancel")}</Button>
                    <Button type="submit" disabled={adminBusy} className="gradient-primary text-primary-foreground">
                      {adminBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "admin" ? "Sign in as Admin" : "Sign in as Moderator")}
                    </Button>
                  </DialogFooter>
                </form>
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>
    </SiteShell>
  );
}
